import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload } from 'lucide-react';
import { HoursEntry } from '../../../types';
import { useOpeningHoursPreview } from '../../../hooks/useDirectorySearch';
import { LISTING_LIMITS, newId } from '../../../utils/listingEdit';
import { sortHoursEntries } from '../../../utils/hoursForm';
import {
  buildHoursXlsxLabels,
  generateHoursXlsx,
  HoursXlsxError,
  parseHoursXlsx,
} from '../../../utils/hoursXlsx';
import WeekHours from '../../directory/WeekHours';
import { SectionTitle } from './formFields';

const BUTTON =
  'inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-[10px] text-sm font-medium transition-opacity disabled:opacity-40';

interface Props {
  hours: HoursEntry[];
  /** Saves the new hours (all of them). */
  onReplace: (hours: HoursEntry[]) => Promise<boolean>;
  saving: boolean;
  /** How one line of hours is shown (same as the list above). */
  renderEntry: (entry: HoursEntry) => React.ReactNode;
}

/** Hours in Excel (plan 5.5): download the current hours, upload a file to replace them all. */
export default function HoursUpload({ hours, onReplace, saving, renderEntry }: Props) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const labels = useMemo(
    () => buildHoursXlsxLabels(i18n.getFixedT('en'), i18n.getFixedT('he')),
    [i18n]
  );
  const fileRef = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [parsed, setParsed] = useState<HoursEntry[] | null>(null);
  const [errors, setErrors] = useState<HoursXlsxError[]>([]);
  const [readFailed, setReadFailed] = useState(false);
  const [done, setDone] = useState(false);
  const preview = useOpeningHoursPreview(parsed ?? [], 'scheduled');

  const reset = () => {
    setParsed(null);
    setErrors([]);
    setReadFailed(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const download = async () => {
    const blob = await generateHoursXlsx(hours, labels, isHebrew);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('directory.admin.hoursUpload.fileName')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDone(false);
    setParsed(null);
    setErrors([]);
    setReadFailed(false);
    setReading(true);
    try {
      const result = await parseHoursXlsx(await file.arrayBuffer(), labels, newId, LISTING_LIMITS.hours);
      setErrors(result.errors);
      setParsed(result.errors.length ? null : result.entries);
    } catch {
      setReadFailed(true);
    } finally {
      setReading(false);
    }
  };

  const replace = async () => {
    if (!parsed || !window.confirm(t('directory.admin.hoursUpload.confirmReplace'))) return;
    if (await onReplace(parsed)) {
      reset();
      setDone(true);
    }
  };

  const errorText = (err: HoursXlsxError) => {
    const where = err.rowNumber > 0 ? t('directory.admin.hoursUpload.row', { row: err.rowNumber }) : t('directory.admin.hoursUpload.wholeFile');
    const column = err.column ? t(`directory.admin.hoursUpload.column.${err.column}`) : '';
    return `${where}: ${t(`directory.admin.hoursUpload.error.${err.code}`, { value: err.value ?? '', column })}`;
  };

  return (
    <section className="space-y-3 pt-2 border-t border-ink/10">
      <SectionTitle>{t('directory.admin.hoursUpload.title')}</SectionTitle>
      <p className="text-sm text-ash">{t('directory.admin.hoursUpload.hint')}</p>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={download} className={`${BUTTON} border border-ink/20 text-ink hover:border-ink/50`}>
          <Download className="w-4 h-4" aria-hidden />
          {t('directory.admin.hoursUpload.download')}
        </button>
        <label className={`${BUTTON} bg-ink text-paper cursor-pointer focus-within:ring-2 focus-within:ring-ink/40`}>
          <Upload className="w-4 h-4" aria-hidden />
          {t('directory.admin.hoursUpload.upload')}
          <input ref={fileRef} type="file" accept=".xlsx" onChange={readFile} className="sr-only" />
        </label>
      </div>

      {reading && <p className="text-sm text-ash">{t('directory.admin.hoursUpload.reading')}</p>}
      {readFailed && <p role="alert" className="text-sm text-danger-600">{t('directory.admin.hoursUpload.readFailed')}</p>}
      {done && <p role="status" className="text-sm text-ink">{t('directory.admin.hoursUpload.done')}</p>}

      {errors.length > 0 && (
        <div role="alert" className="rounded-[16px] border border-danger-100 bg-danger-50 p-3 space-y-1">
          <p className="text-sm font-medium text-danger-700">{t('directory.admin.hoursUpload.errorsTitle')}</p>
          {errors.map((err, i) => (
            <p key={i} className="text-sm text-danger-600">
              <bdi>{errorText(err)}</bdi>
            </p>
          ))}
        </div>
      )}

      {parsed && (
        <div className="rounded-[16px] border border-ink/15 p-3 space-y-3">
          {parsed.length === 0 ? (
            <p className="text-sm font-medium text-danger-600">{t('directory.admin.hoursUpload.emptyWarning')}</p>
          ) : (
            <>
              <p className="text-sm font-medium text-ink">{t('directory.admin.hoursUpload.previewTitle', { count: parsed.length })}</p>
              <ul className="space-y-2">
                {sortHoursEntries(parsed).map((h) => (
                  <li key={h.id} className="rounded-[12px] bg-fog/60 p-2">
                    {renderEntry(h)}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-ash">{t('directory.admin.hoursUpload.previewHint')}</p>
              <WeekHours week={preview.week} />
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={saving} onClick={replace} className={`${BUTTON} bg-ink text-paper`}>
              {t('directory.admin.hoursUpload.replace')}
            </button>
            <button type="button" onClick={reset} className={`${BUTTON} text-ink`}>
              {t('directory.admin.hoursUpload.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
